import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
    AccordionDetails,
    AccordionSummary,
    Box,
    Button,
    CardContent,
    CardHeader,
    Chip,
    Grid,
    IconButton,
    List,
    ListItem,
    ListItemText,
    MenuItem,
    Stack,
    Typography,
} from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import { Accordion, FormCard, Loader, Menu } from '../../../../../../Components';
import { setModal } from '../../../../../Common/App';
import {
    DeleteLocationModal,
    DeleteVenueModal,
    LocationModal,
    VenueModal,
    fetchLocations,
    fetchVenues,
} from '../../../index';
import { usePermissions } from '../../../../../../Hooks/usePermissions';

const sortLocations = (locs) => [...locs].sort((a, b) => {
    const sa = Number(a.sort_order ?? 0);
    const sb = Number(b.sort_order ?? 0);
    if (sa !== sb) {
        return sa - sb;
    }
    return (a.location_name || '').localeCompare(b.location_name || '');
});

const summaryChipSx = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    flexDirection: 'row-reverse',
};

export const VenueTab = () => {

    const dispatch = useDispatch();
    const venuesDirty = useSelector((store) => store.settings?.venues?.is_dirty);
    const locationsDirty = useSelector((store) => store.settings?.locations?.is_dirty);
    const [isLoading, setIsLoading] = useState(true);
    const [items, setItems] = useState([]);
    const [locations, setLocations] = useState([]);
    const [menuState, setMenuState] = useState({});
    const { hasPermission, hasAnyPermission } = usePermissions();

    const locationsByVenueId = useMemo(() => {
        const map = {};
        locations.forEach((loc) => {
            if (!loc.venue_id) {
                return;
            }
            if (!map[loc.venue_id]) {
                map[loc.venue_id] = [];
            }
            map[loc.venue_id].push(loc);
        });
        Object.keys(map).forEach((k) => {
            map[k] = sortLocations(map[k]);
        });
        return map;
    }, [locations]);

    const fetchData = useCallback(async () => {
        setIsLoading(true);
        await Promise.all([
            fetchVenues({
                dispatch,
                filters: {
                    paginate: false,
                    sort_by: 'venue_name',
                    sort_dir: 'asc',
                    columns: ['id', 'venue_name', 'code', 'address', 'notes', 'is_active'],
                },
            }).then((res) => {
                if (res.success) {
                    const data = Array.isArray(res.data) ? res.data : (res.data?.data || []);
                    setItems(data);
                }
            }),
            fetchLocations({
                dispatch,
                filters: {
                    paginate: false,
                    sort_by: 'sort_order',
                    sort_dir: 'asc',
                    columns: ['id', 'venue_id', 'location_name', 'code', 'notes', 'sort_order', 'is_active'],
                },
            }).then((res) => {
                if (res.success) {
                    const data = Array.isArray(res.data) ? res.data : (res.data?.data || []);
                    setLocations(data);
                }
            }),
        ]);
        setIsLoading(false);
    }, [dispatch]);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    useEffect(() => {
        if (venuesDirty || locationsDirty) {
            fetchData();
        }
    }, [venuesDirty, locationsDirty, fetchData]);

    const handleClick = (event, menuKey) => {
        event.stopPropagation();
        setMenuState((prev) => ({
            ...prev,
            [menuKey]: event.currentTarget,
        }));
    };

    const handleClose = (menuKey) => {
        setMenuState((prev) => ({
            ...prev,
            [menuKey]: null,
        }));
    };

    const handleDeleteVenue = (e, row) => {
        e.stopPropagation();
        dispatch(setModal({ component: <DeleteVenueModal venue={row} />, open: true }));
    };

    const handleEditVenue = (e, row) => {
        e.stopPropagation();
        dispatch(setModal({ component: <VenueModal item={row} />, open: true }));
    };

    const handleAddLocation = (e, row) => {
        e.stopPropagation();
        dispatch(setModal({ component: <LocationModal venue={row} />, open: true }));
    };

    const handleEditLocation = (e, row, loc) => {
        e.stopPropagation();
        dispatch(setModal({ component: <LocationModal venue={row} item={loc} />, open: true }));
    };

    const handleDeleteLocation = (e, row, loc) => {
        e.stopPropagation();
        dispatch(setModal({
            component: <DeleteLocationModal location={loc} venueName={row.venue_name} />,
            open: true,
        }));
    };

    if (!hasPermission('core.settings.venue.menu')) {
        return (
            <FormCard>
                <CardHeader title="Venue management" />
                <CardContent>
                    <Typography variant="body2" color="text.secondary">
                        You do not have access to venue management. Ask an administrator to grant the{' '}
                        <strong>core.settings.venue.menu</strong> permission.
                    </Typography>
                </CardContent>
            </FormCard>
        );
    }

    return (
        <FormCard>
            <CardHeader
                title="Venue management"
                avatar={<Loader isLoading={isLoading} type="linear" />}
                action={hasPermission('core.settings.venue.add') ? (
                    <Button
                        variant="contained"
                        size="small"
                        color="info"
                        onClick={() => dispatch(setModal({ component: <VenueModal />, open: true }))}
                    >
                        + New Venue
                    </Button>
                ) : null}
            />
            <CardContent sx={{ pb: '1rem!important' }}>
                <Grid container spacing={2}>
                    {items.length > 0 ? (
                        <Grid item xs={12}>
                            {items.map((row) => {
                                const venueMenuKey = `venue-${row.id}`;
                                const locs = locationsByVenueId[row.id] || [];
                                const statusLabel = row.is_active ? 'Active' : 'Inactive';
                                const venueChipLabel = row.code ? `${row.code} · ${statusLabel}` : statusLabel;

                                return (
                                    <Accordion key={row.id} rounded={0} elevation={0}>
                                        <AccordionSummary
                                            expandIcon={<ExpandMoreIcon />}
                                            aria-controls={`panel-venue-${row.id}-content`}
                                            id={`panel-venue-${row.id}-header`}
                                            sx={summaryChipSx}
                                        >
                                            <Box sx={{ display: 'flex', alignItems: 'center', flexGrow: 1 }}>
                                                <Typography sx={{ flexGrow: 1 }}>{row.venue_name}</Typography>
                                                <Stack direction="row" justifyContent="space-between" spacing={1}>
                                                    <Chip variant="outlined" color="primary" size="small" label={venueChipLabel} />
                                                </Stack>
                                            </Box>
                                            {hasAnyPermission(['core.settings.venue.edit', 'core.settings.venue.delete']) && (
                                                <Box sx={{ display: 'flex', gap: 1 }}>
                                                    <IconButton
                                                        aria-label="more"
                                                        id={`menu-button-${venueMenuKey}`}
                                                        aria-controls={menuState[venueMenuKey] ? `menu-${venueMenuKey}` : undefined}
                                                        aria-expanded={menuState[venueMenuKey] ? 'true' : undefined}
                                                        aria-haspopup="true"
                                                        onClick={(e) => handleClick(e, venueMenuKey)}
                                                    >
                                                        <MoreVertIcon />
                                                    </IconButton>
                                                    <Menu
                                                        id={`menu-${venueMenuKey}`}
                                                        anchorEl={menuState[venueMenuKey]}
                                                        open={Boolean(menuState[venueMenuKey])}
                                                        onClose={() => handleClose(venueMenuKey)}
                                                        anchorOrigin={{
                                                            vertical: 'bottom',
                                                            horizontal: 'right',
                                                        }}
                                                        transformOrigin={{
                                                            vertical: 'top',
                                                            horizontal: 'right',
                                                        }}
                                                    >
                                                        {hasPermission('core.settings.venue.edit') && (
                                                            <MenuItem onClick={(e) => handleEditVenue(e, row)}>
                                                                <EditIcon fontSize="small" sx={{ mr: 1 }} /> Edit venue
                                                            </MenuItem>
                                                        )}
                                                        {hasPermission('core.settings.venue.delete') && (
                                                            <MenuItem onClick={(e) => handleDeleteVenue(e, row)}>
                                                                <DeleteIcon fontSize="small" sx={{ mr: 1 }} /> Delete venue
                                                            </MenuItem>
                                                        )}
                                                    </Menu>
                                                </Box>
                                            )}
                                        </AccordionSummary>
                                        <AccordionDetails>

                                            {locs.map((loc) => {
                                                const locMenuKey = `loc-${loc.id}`;
                                                const locStatus = loc.is_active ? 'Active' : 'Inactive';
                                                const locChipLabel = loc.code ? `${loc.code} · ${locStatus}` : locStatus;

                                                return (
                                                    <Accordion key={loc.id} rounded={0} elevation={0}>
                                                        <AccordionSummary
                                                            expandIcon={<ExpandMoreIcon />}
                                                            aria-controls={`panel-loc-${loc.id}-content`}
                                                            id={`panel-loc-${loc.id}-header`}
                                                            sx={summaryChipSx}
                                                        >
                                                            <Box sx={{ display: 'flex', alignItems: 'center', flexGrow: 1 }}>
                                                                <Typography sx={{ flexGrow: 1 }}>{loc.location_name}</Typography>
                                                                <Stack direction="row" justifyContent="space-between" spacing={1}>
                                                                    <Chip variant="outlined" color="primary" size="small" label={locChipLabel} />
                                                                </Stack>
                                                            </Box>
                                                            {hasAnyPermission(['core.settings.venue.edit', 'core.settings.venue.delete']) && (
                                                                <Box sx={{ display: 'flex', gap: 1 }}>
                                                                    <IconButton
                                                                        aria-label="more"
                                                                        id={`menu-button-${locMenuKey}`}
                                                                        aria-controls={menuState[locMenuKey] ? `menu-${locMenuKey}` : undefined}
                                                                        aria-expanded={menuState[locMenuKey] ? 'true' : undefined}
                                                                        aria-haspopup="true"
                                                                        onClick={(e) => handleClick(e, locMenuKey)}
                                                                    >
                                                                        <MoreVertIcon />
                                                                    </IconButton>
                                                                    <Menu
                                                                        id={`menu-${locMenuKey}`}
                                                                        anchorEl={menuState[locMenuKey]}
                                                                        open={Boolean(menuState[locMenuKey])}
                                                                        onClose={() => handleClose(locMenuKey)}
                                                                        anchorOrigin={{
                                                                            vertical: 'bottom',
                                                                            horizontal: 'right',
                                                                        }}
                                                                        transformOrigin={{
                                                                            vertical: 'top',
                                                                            horizontal: 'right',
                                                                        }}
                                                                    >
                                                                        {hasPermission('core.settings.venue.edit') && (
                                                                            <MenuItem onClick={(e) => handleEditLocation(e, row, loc)}>
                                                                                <EditIcon fontSize="small" sx={{ mr: 1 }} /> Edit
                                                                            </MenuItem>
                                                                        )}
                                                                        {hasPermission('core.settings.venue.delete') && (
                                                                            <MenuItem onClick={(e) => handleDeleteLocation(e, row, loc)}>
                                                                                <DeleteIcon fontSize="small" sx={{ mr: 1 }} /> Delete
                                                                            </MenuItem>
                                                                        )}
                                                                    </Menu>
                                                                </Box>
                                                            )}
                                                        </AccordionSummary>
                                                        <AccordionDetails>
                                                            <List>
                                                                {loc.code ? (
                                                                    <ListItem>
                                                                        <ListItemText primary="Code" secondary={loc.code} />
                                                                    </ListItem>
                                                                ) : null}
                                                            </List>
                                                        </AccordionDetails>
                                                    </Accordion>
                                                );
                                            })}

                                            {locs.length === 0 && (
                                                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                                                    No locations for this venue yet.
                                                </Typography>
                                            )}

                                            {hasPermission('core.settings.venue.add') && (
                                                <Button
                                                    size="small"
                                                    variant="outlined"
                                                    sx={{ mt: 2 }}
                                                    onClick={(e) => handleAddLocation(e, row)}
                                                >
                                                    + Add location
                                                </Button>
                                            )}
                                        </AccordionDetails>
                                    </Accordion>
                                );
                            })}
                        </Grid>
                    ) : (
                        <Grid item xs={12}>
                            <Typography variant="body2" color="text.secondary">
                                No venues yet. Add your first venue to get started.
                            </Typography>
                        </Grid>
                    )}
                </Grid>
            </CardContent>
        </FormCard>
    );
};
